/**
 * This module provides functions in relation to calling API routes on the backend server and processing the incoming
 * responses. The functions in this module refer to the API endpoint ApiEndPoints.WATERFALL.
 * @module
 */

import {ApiEndPoints, HttpMethods} from "../base/Settings";
import type {FetchRequest, WaterfallInput, Waterfall, UUID} from "../base/ui_general/GeneralInterfaces";


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the creation of a new
 * waterfall on the server. Only name, description and waterfall type need to be specified for this purpose.
 * @param waterfallInput An object of type WaterfallInput containing the input data for creating the waterfall.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_create_waterfall(waterfallInput: WaterfallInput): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.WATERFALL,
        method: HttpMethods.POST,
        query_parameters: {},
        body: waterfallInput,
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for updating an existing
 * waterfall on the server.
 * @param waterfall An object of type Waterfall containing the updated waterfall data.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_update_waterfall(waterfall: Waterfall): FetchRequest {
    const waterfallPayload = Object.fromEntries(
        Object.entries(waterfall).filter(
            ([key]) => key !== "waterfall_available_funds_rows" && key !== "waterfall_payment_priority_rows"
        )
    );
    return {
        api_endpoint: ApiEndPoints.WATERFALL,
        method: HttpMethods.PUT,
        query_parameters: {},
        body: waterfallPayload,
    };
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the query of waterfalls
 * on the server.
 * @param transaction_id Optional. The UUID of the transaction to query for.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_get_all_waterfalls(transaction_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.WATERFALL,
        method: HttpMethods.GET,
        query_parameters:  { transaction_id } ,
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for deleting an existing
 * waterfall on the server.
 * @param waterfall_id The UUID of the waterfall to delete.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_delete_waterfall(waterfall_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.WATERFALL,
        method: HttpMethods.DELETE,
        query_parameters: { waterfall_id: waterfall_id },
    };
}
